import {
    PlacementCollectSchema,
    PlacementCollectionOutcomeSchema,
    type PlacementCollection,
    type PlacementCollect,
    type PlacementPriceBasis,
    type ReassignItemResult,
} from '@neon-one/auctions-types';

import { formatCents } from '../format';
import { ReassignMode, type ReassignModeValue } from '../item-actions';
import { offerWindowLabel, resultMessage, wasAppended } from '../reassign-consequences';
import { ReassignAddress, type ReassignSubject, type TargetRow } from '../reassign-target';
import type { PlacementOutcome, SubmitPlacementArgs } from '../types';

const Collect = PlacementCollectSchema.enum;
const Outcome = PlacementCollectionOutcomeSchema.enum;
// The shared shell converts its danger method to PrimeVue's error severity.
const ToastSeverity = { Success: 'success', Info: 'info', Warn: 'warn', Error: 'danger' } as const;

export interface PlacementDecision {
    mode: ReassignModeValue;
    notify: boolean;
    idempotencyKey: string | null;
    enhanced: boolean;
    priceBasis: PlacementPriceBasis;
    priceCents: number | null;
    /** Absent: the invoice is left unpaid for the admin to collect later. */
    collect: PlacementCollect | undefined;
}

interface PlacementPresentation {
    collect: PlacementCollect | undefined;
    unsold: boolean;
    priceCents: number | null;
    invoiceNumber: string | null;
}

interface PlacementToast {
    severity: typeof ToastSeverity[keyof typeof ToastSeverity];
    summary: string;
    detail: string;
}

export function buildPlacementArgs(
    subject: ReassignSubject, row: TargetRow, decision: PlacementDecision,
): SubmitPlacementArgs {
    const fields = {
        targetBidderUuid: row.bidderUuid,
        mode: decision.mode,
        notify: decision.mode === ReassignMode.ReassignNow ? decision.notify : true,
        idempotencyKey: decision.idempotencyKey ?? undefined,
    };
    const price = { priceBasis: decision.priceBasis, priceCents: decision.priceCents ?? 0 };
    const payment = decision.mode === ReassignMode.ReassignNow && decision.collect !== undefined
        ? { collect: decision.collect } : {};
    if (subject.address === ReassignAddress.Lot) return { ...fields, ...price, ...payment, lot: subject.lotRef };
    return { ...fields, lineId: subject.lineId, ...(decision.enhanced ? { ...price, ...payment } : {}) };
}

export function describePlacement(
    row: TargetRow, result: ReassignItemResult, invoiceNumber: string | null,
): string {
    const offerMode = result.mode === ReassignMode.SendOffer;
    return resultMessage({
        mode: result.mode,
        name: row.name,
        appended: wasAppended({ newInvoiceUuid: result.newInvoiceUuid, openInvoiceUuid: row.openInvoiceUuid }),
        invoiceNumber: offerMode ? row.openInvoiceNumber : (invoiceNumber ?? row.openInvoiceNumber),
        windowLabel: offerWindowLabel(result.offer?.expiresAt ?? null),
    });
}

function saleDescription(
    row: TargetRow, result: ReassignItemResult | null, presentation: PlacementPresentation,
): string {
    if (result?.mode === ReassignMode.SendOffer) return describePlacement(row, result, presentation.invoiceNumber);
    const verb = presentation.unsold ? 'Sold' : 'Reassigned';
    const amount = formatCents(result?.amountCents ?? presentation.priceCents ?? row.amountCents);
    const number = presentation.invoiceNumber ?? row.openInvoiceNumber;
    return `${verb} to ${row.name} at ${amount}${number ? ` on ${number}` : ''}.`;
}

function refusalToast(collect: PlacementCollect | undefined, reason: PlacementCollection['reason']): PlacementToast {
    if (collect === Collect.charge) {
        return { severity: ToastSeverity.Error, summary: 'Card not charged',
            detail: `Card not charged: ${reason ?? 'the card could not be charged'}. Send the invoice instead.` };
    }
    return { severity: ToastSeverity.Error, summary: 'Invoice email not sent',
        detail: reason ?? 'We could not send the invoice email.' };
}

export function placementToast(
    row: TargetRow, outcome: PlacementOutcome, presentation: PlacementPresentation,
): PlacementToast {
    const collection = outcome.collection ?? outcome.result?.collection;
    const detail = saleDescription(row, outcome.result, presentation);
    const total = formatCents(collection?.chargedTotalCents ?? outcome.result?.amountCents ?? presentation.priceCents ?? 0);
    const verb = presentation.unsold ? 'Sold' : 'Reassigned';
    const success = { severity: ToastSeverity.Success, summary: `${verb} to ${row.name}`, detail };
    switch (collection?.outcome) {
        case Outcome.charged:
            return { ...success, detail: `${detail} Charged ${total}.` };
        case Outcome.submitted:
            return { severity: ToastSeverity.Info, summary: 'Charge submitted',
                detail: `Charge submitted for ${total}. The invoice updates when it settles.` };
        case Outcome.declined:
            return { severity: ToastSeverity.Warn, summary: 'Card declined', detail: `${detail} Their card was declined,`
                + ' so the invoice is marked declined. They can pay another way.' };
        case Outcome.refused:
            return refusalToast(presentation.collect, collection.reason);
        case Outcome.unconfirmed:
            return { severity: ToastSeverity.Warn, summary: 'Charge not confirmed',
                detail: "We couldn't confirm the charge yet. Don't charge again; the invoice updates when we hear back." };
        case Outcome.notified:
            return { ...success, detail: `${detail} Checkout link sent.` };
        default:
            return success;
    }
}
